import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  notification,
} from "antd";
import { CopyOutlined, UploadOutlined } from "@ant-design/icons";
import { useContext, useMemo, useState } from "react";

import { Context } from "../../../context";
import { Post } from "../../../fetch";
import { useBank } from "./BankContext";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const TYPES = ["translation", "fill_blank", "synonym", "antonym", "grammar"];

/** Adaptive grades (setu quiz_gen._GRADE_GUIDE — keep in sync). */
const GRADES = [
  { value: null, label: "auto (by level)" },
  { value: 1, label: "1 — A1 beginner" },
  { value: 2, label: "2 — A2 simple" },
  { value: 3, label: "3 — B1 aspirant" },
  { value: 4, label: "4 — B2 exam-grade" },
  { value: 5, label: "5 — C1 toughest" },
];

const GRADE_PROMPT = {
  1: "grade 1/5 — CEFR A1: beginner everyday words, simple present, very short sentences",
  2: "grade 2/5 — CEFR A2: simple past/future, common phrases, short sentences",
  3: "grade 3/5 — CEFR B1: idioms, tense forms, prepositions, bank/SSC aspirant vocabulary",
  4: "grade 4/5 — CEFR B2: exam-grade vocabulary, subtle grammar, tempting distractors",
  5: "grade 5/5 — CEFR C1: close synonym discrimination, nuanced usage, trap distractors",
};

const FORMAT_EXAMPLE = `[
  {
    "type": "translation",
    "question_hi": "'मैं कल बाज़ार जाऊँगा' का सही अंग्रेज़ी अनुवाद चुनिए।",
    "question_en": "Choose the correct English translation.",
    "options": ["I will go to the market tomorrow", "I go to market tomorrow", "I went to the market tomorrow", "I am going market tomorrow"],
    "correct_index": 0,
    "explanation_hi": "भविष्य काल के लिए 'will go' सही है।"
  },
  {
    "type": "synonym",
    "question_hi": "'Abundant' का समानार्थी शब्द चुनिए।",
    "question_en": null,
    "options": ["Plentiful", "Scarce", "Rare", "Limited"],
    "correct_index": 0,
    "explanation_hi": "'Abundant' का अर्थ है प्रचुर — 'Plentiful' समानार्थी है।"
  }
]`;

const REASONING_FORMAT_EXAMPLE = `[
  {
    "type": "series",
    "question_hi": "श्रृंखला पूरी कीजिए: 2, 6, 12, 20, ?",
    "question_en": "Complete the series: 2, 6, 12, 20, ?",
    "options": ["30", "28", "32", "26"],
    "options_en": ["30", "28", "32", "26"],
    "correct_index": 0,
    "explanation_hi": "अंतर 4, 6, 8 बढ़ता है — अगला अंतर 10, इसलिए 30।",
    "explanation_en": "Differences grow 4, 6, 8 — next is 10, so 30."
  }
]`;

const MATH_FORMAT_EXAMPLE = `[
  {
    "type": "percentage",
    "question_hi": "1200 का 15% कितना है?",
    "question_en": "What is 15% of 1200?",
    "options": ["150", "180", "200", "120"],
    "options_en": ["150", "180", "200", "120"],
    "correct_index": 1,
    "expression": "1200*15/100",
    "explanation_hi": "1200 × 15 / 100 = 180।",
    "explanation_en": "1200 × 15 / 100 = 180."
  }
]`;

/** Math-bank prompt (mirrors quiz_gen._build_prompt_math) — bilingual plus a
 * machine-checkable `expression`; setu recomputes it and DROPS any item
 * whose keyed option does not match. */
const buildPromptMath = (level, topic, count, types, difficulty, allTypes) => {
  const typeGuide = {
    arithmetic: "BODMAS / mixed operations on integers, decimals and fractions",
    percentage: "percent of a number, percent change, successive percentages",
    ratio_proportion: "divide in a ratio, find the missing term, compound ratios",
    profit_loss: "cost/selling price, profit or loss percent, discount on marked price",
    simple_interest: "SI = P*R*T/100 — find interest, principal, rate or time",
    average: "average of a set, a new member changing the average, weighted average",
    number_system: "LCM, HCF, divisibility rules, remainders, unit digit",
    time_speed_distance: "speed = distance/time, relative speed, trains/platforms",
    time_work: "A finishes in x days, B in y — together / alone / pipes",
    algebra_basic: "solve a linear equation or simple system for x",
  };
  const chosen = types.length ? types : allTypes;
  return [
    "You are creating quantitative-aptitude (maths) quiz questions for",
    "Indian users preparing for competitive exams (SSC, banking, railways).",
    `This bank is for level ${level} of a progressive ladder.`,
    `Difficulty: ${
      difficulty ? GRADE_PROMPT[difficulty] : "match the level (higher level = harder)"
    }.`,
    `Topic focus: ${topic || "general arithmetic"}.`,
    "",
    `Generate exactly ${count} multiple-choice questions, mixing these types:`,
    ...chosen.map((t) => `- ${t}: ${typeGuide[t]}`),
    "",
    "EVERY question is fully BILINGUAL — complete Hindi (Devanagari) AND",
    "complete English versions of the question, all four options, and the",
    "explanation. Options in both languages MUST be in the same order with",
    "the same correct_index. Numeric options repeat unchanged in both. Use",
    "Western digits (0-9) everywhere, never Devanagari numerals.",
    "",
    "Maths rules:",
    "- The correct answer is a single NUMBER. Write the exact arithmetic that",
    '  produces it in `expression` using only numbers, + - * / ( ) and ** for',
    '  powers (e.g. "1200*15/100", "(45+55+62)/3"). We recompute `expression`',
    "  and DROP the question if it does not equal the keyed option.",
    '- Options are plain text: "1,800", "25%", "3/4", "12.5 km/h", "x = 7".',
    "  No LaTeX, no superscripts, no Devanagari digits.",
    "- Exactly ONE correct option; no two options may share a numeric value.",
    "- Distractors come from classic mis-steps, never random numbers.",
    "- Every question uses DIFFERENT numbers and setup — no near-duplicates.",
    "- Spread correct_index roughly evenly across 0-3 over the set.",
    "",
    "Format rules:",
    "- question_hi (required), question_en (required).",
    "- options: 4 distinct Hindi options; options_en: same 4 in English.",
    "- correct_index: 0-based index of the correct option (both lists).",
    "- expression: the arithmetic for the correct answer (required).",
    "- explanation_hi and explanation_en: one short sentence showing the working.",
    `- type: one of ${JSON.stringify(chosen)}.`,
    "- No numbering, no duplicates, no offensive or political content.",
    "",
    "Return ONLY a JSON array (no prose, no markdown fences) of objects with",
    'keys: "type", "question_hi", "question_en", "options", "options_en",',
    '"correct_index", "expression", "explanation_hi", "explanation_en".',
  ].join("\n");
};

/** Mirrors setu quiz_gen.infer_reasoning_types — empty Types locks to topic. */
const inferReasoningTypes = (topic, types, allTypes) => {
  if (types.length) return types;
  const text = (topic || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  if (!text) return allTypes.filter((t) => t !== "general");
  if (/^(general )?reasoning$|^logical reasoning$/.test(text)) {
    return allTypes.filter((t) => t !== "general");
  }
  if (
    /\b(gk|g k|general knowledge|current affairs?|static gk|history|geography|science|sports?|polity|civics|english|hindi)\b/.test(
      text
    )
  ) {
    return ["general"];
  }
  const family = [
    ["odd one out", "odd_one_out"],
    ["odd one", "odd_one_out"],
    ["blood relation", "blood_relation"],
    ["direction sense", "direction_sense"],
    ["coding decoding", "coding_decoding"],
    ["logical deduction", "logical_deduction"],
    ["number series", "series"],
    ["letter series", "series"],
    ["mental arithmetic", "arithmetic"],
    ["syllog", "logical_deduction"],
    ["analog", "analogy"],
    ["decoding", "coding_decoding"],
    ["coding", "coding_decoding"],
    ["direction", "direction_sense"],
    ["percent", "arithmetic"],
    ["ratio", "arithmetic"],
    ["arithmetic", "arithmetic"],
    ["series", "series"],
  ];
  const hit = family.find(([needle]) => text.includes(needle));
  return hit ? [hit[1]] : ["general"];
};

/** Reasoning-bank prompt (mirrors quiz_gen._build_prompt_reasoning) —
 * BILINGUAL output: hi + en for question, options and explanation.
 * Topic is a hard lock — empty Types does NOT mix all families. */
const buildPromptReasoning = (level, topic, count, types, difficulty, allTypes) => {
  const typeGuide = {
    series: "continue a number/letter series (e.g. 2, 6, 12, 20, ?)",
    analogy: "A : B :: C : ? — word, number, or letter analogies",
    odd_one_out: "pick the item that does not belong in the group",
    logical_deduction: "syllogism / statement-conclusion — which conclusion follows",
    arithmetic: "quick mental arithmetic or percentage/ratio reasoning",
    direction_sense: "start point, moves, final direction/distance",
    blood_relation: "family-relation chains",
    coding_decoding: "letter/number coding schemes",
    general:
      "factual multiple-choice on the given topic only — GK, current affairs, history, geography, science, sports, language. Not a reasoning puzzle unless the topic itself is one",
  };
  const chosen = inferReasoningTypes(topic, types, allTypes);
  const topicText = topic || "general reasoning";
  return [
    "You are creating quiz questions for Indian users of the Gyanni app",
    "preparing for competitive exams (SSC, banking, railways) and school GK.",
    `This bank is for level ${level} of a progressive ladder.`,
    `Difficulty: ${
      difficulty ? GRADE_PROMPT[difficulty] : "match the level (higher level = harder)"
    }.`,
    "",
    "HARD CONSTRAINT — TOPIC LOCK:",
    `Every question MUST be about this topic only: "${topicText}".`,
    "Do NOT generate questions from any other subject, exam family, or",
    "reasoning type. If the topic is general knowledge / GK / current",
    "affairs / history / geography / science / sports / polity / language,",
    "write factual MCQs on that subject. Do NOT fill the set with number",
    "series, analogies, coding-decoding, blood relations, direction sense,",
    "odd-one-out, or other unrelated reasoning puzzles.",
    "If the topic itself IS a reasoning family (number series, blood",
    "relations, ...), write only that family.",
    "",
    `Generate exactly ${count} multiple-choice questions.`,
    "Use only these type labels (do not invent types, do not mix extra families):",
    ...chosen.map((t) => `- ${t}: ${typeGuide[t] || t}`),
    "",
    "EVERY question is fully BILINGUAL — complete Hindi (Devanagari) AND",
    "complete English versions of the question, all four options, and the",
    "explanation. Options in both languages MUST be in the same order with",
    "the same correct_index. Numeric options repeat unchanged in both.",
    "",
    "Question quality rules:",
    "- Exactly ONE defensibly correct answer; verify it before writing options.",
    "- Distractors are plausible for this topic, never random values.",
    "- Every question must test a DIFFERENT fact or pattern — no near-duplicates.",
    "- Spread correct_index roughly evenly across 0-3 over the set.",
    "",
    "Format rules:",
    "- question_hi (required), question_en (required).",
    "- options: 4 distinct Hindi options; options_en: same 4 in English.",
    "- correct_index: 0-based index of the correct option (both lists).",
    "- explanation_hi and explanation_en: one short sentence each.",
    `- type: one of ${JSON.stringify(chosen)}.`,
    "- No numbering, no duplicates, no offensive or political content.",
    "",
    "Return ONLY a JSON array (no prose, no markdown fences) of objects with",
    'keys: "type", "question_hi", "question_en", "options", "options_en",',
    '"correct_index", "explanation_hi", "explanation_en".',
  ].join("\n");
};

/** Mirrors the backend Gemini prompt (app/helper/quiz_gen.py _build_prompt)
 * so hand-generated banks match the same quality + format rules. */
const buildPrompt = (level, topic, count, types, difficulty) => {
  const typeGuide = {
    translation:
      "translate a Hindi word/phrase/sentence into English (options are English)",
    fill_blank:
      "an English sentence with one blank; Hindi hint in the question (options are English words)",
    synonym: "pick the English synonym of a given English word",
    antonym: "pick the English antonym of a given English word",
    grammar: "pick the grammatically correct English sentence/form",
  };
  const chosen = types.length ? types : TYPES;
  return [
    "You are creating Hindi-to-English language-learning quiz questions for",
    "Indian users preparing for competitive exams (SSC, banking, railways).",
    `This bank is for level ${level} of a progressive ladder.`,
    `Difficulty: ${
      difficulty ? GRADE_PROMPT[difficulty] : "match the level (higher level = harder)"
    }.`,
    `Topic focus: ${topic || "general English learning"}.`,
    "",
    `Generate exactly ${count} multiple-choice questions, mixing these types:`,
    ...chosen.map((t) => `- ${t}: ${typeGuide[t]}`),
    "",
    "Question quality rules:",
    "- Every question must test something DIFFERENT — no two questions on",
    "  the same word, phrase, or grammar point.",
    "- Distractors: same part of speech and register as the answer,",
    "  plausible to a learner at this difficulty; never joke options,",
    "  never 'all/none of the above'.",
    "- Spread correct_index roughly evenly across 0-3 over the set.",
    "- Avoid textbook cliches (e.g. 'The cat sat on the mat').",
    "",
    "Format rules:",
    "- question_hi: the question text in Hindi (Devanagari). Required.",
    "- question_en: optional short English rendering of the question, or null.",
    "- options: exactly 4 distinct plausible options; exactly ONE correct.",
    "- correct_index: 0-based index of the correct option.",
    "- explanation_hi: one short Hindi sentence explaining the answer.",
    `- type: one of ${JSON.stringify(chosen)}.`,
    "- No numbering, no duplicate questions, no offensive or political content.",
    "",
    "Return ONLY a JSON array (no prose, no markdown fences) of objects with",
    'keys: "type", "question_hi", "question_en", "options", "correct_index",',
    '"explanation_hi".',
  ].join("\n");
};

const ImportTab = () => {
  const { token, apiUrl } = useContext(Context);
  const bankInfo = useBank();
  const bankTypes = bankInfo.types;

  const [level, setLevel] = useState(1);
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(20);
  const [types, setTypes] = useState([]);
  const [difficulty, setDifficulty] = useState(null);
  const [raw, setRaw] = useState("");
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const prompt = useMemo(() => {
    if (bankInfo.bank === "math") {
      return buildPromptMath(level, topic.trim(), count, types, difficulty, bankTypes);
    }
    if (bankInfo.bilingual) {
      return buildPromptReasoning(level, topic.trim(), count, types, difficulty, bankTypes);
    }
    return buildPrompt(level, topic.trim(), count, types, difficulty);
  }, [level, topic, count, types, difficulty, bankInfo.bank, bankInfo.bilingual, bankTypes]);

  const formatExample =
    bankInfo.bank === "math"
      ? MATH_FORMAT_EXAMPLE
      : bankInfo.bilingual
      ? REASONING_FORMAT_EXAMPLE
      : FORMAT_EXAMPLE;

  // Client-side pre-parse so obvious mistakes never hit the API.
  const parsed = useMemo(() => {
    if (!raw.trim()) return { questions: null, error: null };
    try {
      // Tolerate the common LLM habit of wrapping output in ```json fences.
      const cleaned = raw.trim().replace(/^```(?:json)?/i, "").replace(/```$/, "").trim();
      const data = JSON.parse(cleaned);
      const list = Array.isArray(data) ? data : data.questions;
      if (!Array.isArray(list) || !list.length) {
        return { questions: null, error: "Expected a JSON array of question objects (or {\"questions\": [...]})." };
      }
      return { questions: list, error: null };
    } catch (e) {
      return { questions: null, error: `Invalid JSON: ${e.message}` };
    }
  }, [raw]);

  const doImport = () => {
    if (!parsed.questions) return;
    setImporting(true);
    Post(
      `${apiUrl}/v4/quiz-config/questions/import`,
      token,
      {
        level,
        topic: topic.trim() || null,
        bank: bankInfo.bank,
        difficulty_grade: difficulty ?? null,
        questions: parsed.questions,
      },
      (result) => {
        setImporting(false);
        if (result && result.success) {
          setLastResult(result);
          notification.success({
            message: `Imported ${result.inserted} of ${result.total} as drafts`,
          });
          if (result.inserted) setRaw("");
        } else {
          notification.error({
            message:
              (result && typeof result.detail === "string" && result.detail) ||
              "Import failed",
          });
        }
      },
      () => {
        setImporting(false);
        notification.error({ message: "Import failed" });
      }
    );
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => notification.success({ message: `${label} copied` }),
      () => notification.error({ message: "Copy failed" })
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Generation prompt (for ChatGPT / Gemini / Claude)" size="small">
        <Text type="secondary">
          Set the target level/topic below, copy this prompt into any LLM, then
          paste its JSON output into the import box. The prompt matches the
          same quality and format rules the built-in Gemini generator uses.
        </Text>
        <Space size="large" wrap align="end" style={{ marginTop: 12 }}>
          <Space direction="vertical" size={2}>
            <Text>Level</Text>
            <InputNumber min={1} value={level} onChange={(v) => setLevel(v || 1)} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Topic (unique angle = unique questions)</Text>
            <Input
              style={{ width: 320 }}
              placeholder="e.g. cricket commentary English, Bollywood dialogues, railway announcements"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Count</Text>
            <InputNumber min={1} max={200} value={count} onChange={(v) => setCount(v || 1)} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Types (empty = mix of all)</Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: 320 }}
              value={types}
              onChange={setTypes}
              options={bankTypes.map((t) => ({ value: t, label: t }))}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Difficulty grade</Text>
            <Select
              style={{ width: 190 }}
              value={difficulty}
              onChange={setDifficulty}
              options={GRADES}
            />
          </Space>
        </Space>
        <Paragraph
          style={{
            marginTop: 12,
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            fontSize: 12,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            padding: 12,
            maxHeight: 260,
            overflow: "auto",
          }}
        >
          {prompt}
        </Paragraph>
        <Space>
          <Button icon={<CopyOutlined />} onClick={() => copy(prompt, "Prompt")}>
            Copy prompt
          </Button>
          <Button icon={<CopyOutlined />} onClick={() => copy(formatExample, "Format example")}>
            Copy JSON format example
          </Button>
        </Space>
      </Card>

      <Card title="Import questions (JSON)" size="small">
        <Text type="secondary">
          Paste a JSON array of question objects (the format above). Items are
          validated one by one — bad items are skipped and reported, near
          duplicates of the level's existing bank are dropped.
          {bankInfo.verifiedMath &&
            " Math items must carry `expression`; the server recomputes it and skips any item whose keyed option does not match."}
          {" "}Everything lands
          as <Text strong>draft</Text> with source "import"; approve in the
          Review Queue before it reaches users. Import uses the Level, Topic
          and Difficulty selected above.
        </Text>
        <TextArea
          style={{ marginTop: 12, fontFamily: "monospace" }}
          rows={12}
          placeholder={formatExample}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        {parsed.error && (
          <Alert style={{ marginTop: 8 }} type="error" showIcon message={parsed.error} />
        )}
        {parsed.questions && (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message={`${parsed.questions.length} question(s) parsed — will import to level ${level}.`}
          />
        )}
        {lastResult && (
          <Alert
            style={{ marginTop: 8 }}
            type={lastResult.inserted ? "success" : "warning"}
            showIcon
            message={`Last import: ${lastResult.inserted} inserted, ${lastResult.skippedDuplicate} duplicate, ${(lastResult.skippedInvalid || []).length} invalid${
              (lastResult.skippedInvalid || []).length
                ? ` (item index: ${lastResult.skippedInvalid.join(", ")})`
                : ""
            }`}
          />
        )}
        <div style={{ marginTop: 12 }}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            disabled={!parsed.questions}
            loading={importing}
            onClick={doImport}
          >
            Import as drafts
          </Button>
        </div>
      </Card>
    </Space>
  );
};

export default ImportTab;
